import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAPID public key - will be fetched from server if possible
let VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

// Fetch VAPID key from server
const fetchVapidKey = async () => {
  try {
    const response = await axios.get(`${API}/push/vapid-key`);
    if (response.data.publicKey) {
      VAPID_PUBLIC_KEY = response.data.publicKey;
    }
  } catch (error) {
    console.log('Using default VAPID key');
  }
};

// Initialize on load
fetchVapidKey();

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return { success: false, error: 'Notifications not supported' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Register service worker and subscribe to push
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported on this browser');
    return { success: false, error: 'Push notifications not supported' };
  }
  
  try {
    console.log('Registering service worker...');
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    console.log('Service worker registered, waiting for ready state...');
    await navigator.serviceWorker.ready;
    console.log('Service worker ready');
    
    // Request permission if needed
    if (Notification.permission !== 'granted') {
      console.log('Requesting notification permission...');
      const permResult = await requestNotificationPermission();
      if (!permResult.success) {
        console.log('Permission denied by user');
        return { success: false, error: 'Permission denied' };
      }
    }
    
    console.log('Subscribing to push with VAPID key...');
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    console.log('Push subscription created, sending to server...');
    // Send subscription to server
    const response = await axios.post(`${API}/push/subscribe`, {
      subscription: subscription.toJSON()
    });
    
    console.log('Push subscription saved on server:', response.data);
    return { success: true, subscription: response.data };
  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, error: error.message };
  }
};

// Unsubscribe from push
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      await axios.post(`${API}/push/unsubscribe`, {
        endpoint: subscription.endpoint
      });
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Check if already subscribed
export const isSubscribedToPush = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
};

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush
};
